// apps/web/src/components/painel/home/numero-ao-vivo.tsx — Fase 17.8, Plano 03 (D-14, D-19; UI-SPEC U-23).
// Número com contagem inicial (0 -> valor em 480 ms, só na primeira carga) e rolo de dígitos (240 ms, só as colunas que
// mudam, só em atualização ao vivo). SSR e primeiro render escrevem o valor final (sem salto de layout). Com movimento
// reduzido o valor final é escrito de uma vez. O visual é aria-hidden; um span sr-only leva o valor final completo.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { usePrefersReducedMotion } from '../../../hooks/use-prefers-reduced-motion';
import { colunasDoRolo, valorDaContagem, type ColunaDoRolo } from '../../../lib/painel/home/numero-animado';
import '../../../styles/painel-movimento.css';

const DURACAO_CONTAGEM_MS = 480;
const DURACAO_ROLO_MS = 240;

/** Origem da mudança de valor: define se há contagem, rolo ou troca silenciosa. */
export type OrigemNumero = 'primeira-carga' | 'silenciosa' | 'ao-vivo';

export interface NumeroAoVivoProps {
  readonly valor: number;
  readonly formatar: (valor: number) => string;
  readonly unidade?: string;
  readonly prefixo?: string;
  readonly origem: OrigemNumero;
  readonly className?: string;
  /** O elemento pai já tem nome acessível com o valor (ex.: o link da etapa da esteira): omite o span sr-only e não duplica a leitura. */
  readonly nomeAcessivelNoPai?: boolean;
}

// useLayoutEffect no navegador (evita o quadro com o valor final antes da contagem); useEffect no servidor.
const useEfeitoAntesDaPintura = typeof window === 'undefined' ? useEffect : useLayoutEffect;

interface Rolo {
  readonly colunas: readonly ColunaDoRolo[];
  readonly rolando: boolean;
}

function Colunas({ rolo }: { readonly rolo: Rolo }): ReactElement {
  return (
    <span className="numero-rolo">
      {rolo.colunas.map((coluna, indice) =>
        coluna.muda ? (
          <span key={indice} className="numero-rolo__celula">
            <span className="numero-rolo__coluna" style={{ transform: rolo.rolando ? 'translateY(-50%)' : 'translateY(0)' }}>
              <span>{coluna.de}</span>
              <span>{coluna.para}</span>
            </span>
          </span>
        ) : (
          <span key={indice}>{coluna.para}</span>
        ),
      )}
    </span>
  );
}

export function NumeroAoVivo({ valor, formatar, unidade, prefixo = '', origem, className = '', nomeAcessivelNoPai = false }: NumeroAoVivoProps): ReactElement {
  const reduzido = usePrefersReducedMotion();
  const textoFinal = formatar(valor);
  const [texto, setTexto] = useState<string>(textoFinal);
  const [rolo, setRolo] = useState<Rolo | null>(null);
  const formatarRef = useRef(formatar);
  formatarRef.current = formatar;
  const ultimoTexto = useRef<string>(textoFinal);
  const jaContou = useRef<boolean>(false);

  useEfeitoAntesDaPintura(() => {
    const alvo = formatarRef.current(valor);
    const anterior = ultimoTexto.current;
    ultimoTexto.current = alvo;

    if (reduzido || origem === 'silenciosa') {
      setTexto(alvo);
      setRolo(null);
      return undefined;
    }

    if (origem === 'primeira-carga') {
      if (jaContou.current) {
        setTexto(alvo);
        return undefined;
      }
      jaContou.current = true;
      let quadro = 0;
      let concluiu = false;
      const inicio = performance.now();
      const passo = (agora: number): void => {
        const progresso = Math.min(1, (agora - inicio) / DURACAO_CONTAGEM_MS);
        setTexto(progresso >= 1 ? alvo : formatarRef.current(valorDaContagem(valor, progresso)));
        if (progresso < 1) quadro = requestAnimationFrame(passo);
        else concluiu = true;
      };
      setTexto(formatarRef.current(0));
      quadro = requestAnimationFrame(passo);
      return () => {
        cancelAnimationFrame(quadro);
        // Interrompida (ex.: remontagem do modo estrito): a próxima execução conta de novo.
        if (!concluiu) jaContou.current = false;
      };
    }

    // ao-vivo: rola só as colunas que mudaram.
    if (alvo === anterior) {
      setTexto(alvo);
      return undefined;
    }
    setTexto(alvo);
    setRolo({ colunas: colunasDoRolo(anterior, alvo), rolando: false });
    const quadro = requestAnimationFrame(() => requestAnimationFrame(() => setRolo((atual) => (atual === null ? null : { ...atual, rolando: true }))));
    const fim = setTimeout(() => setRolo(null), DURACAO_ROLO_MS + 80);
    return () => {
      cancelAnimationFrame(quadro);
      clearTimeout(fim);
    };
  }, [valor, origem, reduzido]);

  const completo = `${prefixo}${textoFinal}${unidade === undefined ? '' : ` ${unidade}`}`;
  return (
    <span className={`num tabular-nums ${className}`.trim()}>
      {prefixo !== '' && <span aria-hidden="true">{prefixo}</span>}
      {rolo === null ? <span aria-hidden="true">{texto}</span> : <span aria-hidden="true"><Colunas rolo={rolo} /></span>}
      {unidade !== undefined && <span aria-hidden="true" className="ml-1">{unidade}</span>}
      {!nomeAcessivelNoPai && <span className="sr-only">{completo}</span>}
    </span>
  );
}
